// Auth adapter (frontend only). Replaceable: everything the UI needs goes through
// this module. See AUTH_INTEGRATION.md for the backend contract.
//
// Endpoints are CONFIGURED, not assumed. In api mode, if a URL is not set, the
// UI shows "Sign-in isn't set up in this build" instead of calling an invented route.
//   VITE_AUTH_LOGIN_URL    full-page redirect target (planned: /api/auth/login)
//   VITE_AUTH_SESSION_URL  session probe (planned: /api/me)
//   VITE_AUTH_LOGOUT_URL   sign-out target (unconfirmed)
//   VITE_AUTH_PROFILE_URL  first-login profile save (unconfirmed)
// Callback errors return to the app as ?auth_error=<code> or #/signin?e=<code>
// (the Library sign-in spec), with <code> = cancelled|expired|failed|suspended.
// Contract status: P2031-S-UGAIN-BACKEND-CONTRACT-2026-11-05.md (all PROPOSED).
import { isDemo } from '../lib/clock';

/** Header the backend requires on every state-changing call (CSRF, with an Origin check). */
export const CSRF_HEADER = { 'X-UGAIN-Request': '1' } as const;

export type AuthNotice = 'cancelled' | 'expired' | 'failed' | 'suspended' | 'unconfigured';
export interface SessionUser { id?: string; name?: string; email?: string; needsProfile?: boolean }
export type SessionResult =
  | { status: 'signedIn'; user: SessionUser }
  | { status: 'signedOut' }
  | { status: 'suspended' }
  | { status: 'error' };

/** `affiliations`: zero or more catalogue series ids; `primary` is one of them, or '' for none. */
export interface ProfileDraft { name: string; city: string; affiliations: string[]; primary: string; bio: string; publicProfile: boolean }

const env = import.meta.env;
export const authConfig = {
  login: env.VITE_AUTH_LOGIN_URL || '',
  session: env.VITE_AUTH_SESSION_URL || '',
  logout: env.VITE_AUTH_LOGOUT_URL || '',
  profile: env.VITE_AUTH_PROFILE_URL || '',
  /** GET → { mode: 'google' | 'local-dev' | 'unconfigured' }, so the UI never calls a local test login "Google". */
  mode: env.VITE_AUTH_MODE_URL || '',
};

export type ServerAuthMode = 'google' | 'local-dev' | 'unconfigured';
/** Which sign-in the server actually runs. Demo and unknown → null. */
export async function fetchAuthMode(): Promise<ServerAuthMode | null> {
  if (isDemo || !authConfig.mode) return null;
  try {
    const res = await fetch(authConfig.mode, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const b = (await res.json()) as { mode?: string };
    return b.mode === 'google' || b.mode === 'local-dev' || b.mode === 'unconfigured' ? b.mode : null;
  } catch {
    return null;
  }
}

const DEMO_KEY = 'ugain.demo.signedIn';
const DEMO_PROFILE = 'ugain.demo.profileDone';
const HAD_SESSION = 'ugain.hadSession';
const NOTICES: readonly AuthNotice[] = ['cancelled', 'expired', 'failed', 'suspended'];

const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* blocked */ } },
  del: (k: string) => { try { localStorage.removeItem(k); } catch { /* blocked */ } },
};

const SIGNIN_HASH = /^#\/signin(?:\?(.*))?$/;

/**
 * Map the login callback's error to a UI notice: ?auth_error=<code> in the query,
 * or #/signin?e=<code> in the hash. Unknown values → generic failure.
 */
export function parseAuthNotice(search: string, hash = ''): AuthNotice | null {
  const fromHash = SIGNIN_HASH.exec(hash);
  const v = new URLSearchParams(search).get('auth_error') ?? (fromHash ? new URLSearchParams(fromHash[1] ?? '').get('e') ?? 'failed' : null);
  if (!v) return null;
  return (NOTICES as readonly string[]).includes(v) ? (v as AuthNotice) : 'failed';
}

/** Remove the callback error (query or #/signin hash) so a reload does not show the notice again. */
export function clearAuthNotice(): void {
  const u = new URL(window.location.href);
  const inQuery = u.searchParams.has('auth_error');
  const inHash = SIGNIN_HASH.test(u.hash);
  if (!inQuery && !inHash) return;
  u.searchParams.delete('auth_error');
  if (inHash) u.hash = '#/home';
  window.history.replaceState(null, '', u.toString());
}

/**
 * The route to come back to after Google, as a hash route (`#/activity/london`).
 * The backend accepts only same-app hash routes (`^#/[A-Za-z0-9/_-]{0,128}$`) and
 * falls back to #/home, so send exactly that form.
 */
export function returnToFor(hash: string): string {
  return /^#\/[A-Za-z0-9/_-]{0,128}$/.test(hash) && !SIGNIN_HASH.test(hash) ? hash : '#/home';
}

interface MeBody {
  // Library spec shape: { member: { id, displayName, status }, roles, leadOf, isNew }
  member?: { id?: string; displayName?: string; status?: string };
  isNew?: boolean;
  // Flat shape from the design handoff (AUTH_INTEGRATION.md)
  id?: string; name?: string; needsProfile?: boolean; profileComplete?: boolean;
}

/** Map a session-probe response to a session result. Accepts both proposed shapes. Exported for tests. */
export async function interpretMe(res: Pick<Response, 'ok' | 'status' | 'json'>): Promise<SessionResult> {
  if (res.ok) {
    const b = ((await res.json().catch(() => ({}))) ?? {}) as MeBody;
    if (b.member?.status === 'suspended') return { status: 'suspended' };
    const needsProfile = b.needsProfile ?? b.isNew ?? (b.profileComplete === false);
    return { status: 'signedIn', user: { id: b.member?.id ?? b.id, name: b.member?.displayName ?? b.name, needsProfile } };
  }
  if (res.status === 401) return { status: 'signedOut' };
  if (res.status === 403) {
    const body = (await res.json().catch(() => ({}))) as { status?: string; code?: string };
    if (body?.status === 'suspended' || body?.code === 'suspended') return { status: 'suspended' };
  }
  return { status: 'error' };
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function checkSession(): Promise<SessionResult> {
  if (isDemo) {
    await wait(500);
    return store.get(DEMO_KEY) === '1'
      ? { status: 'signedIn', user: { id: 'SR', name: 'Sofia Reyes', needsProfile: store.get(DEMO_PROFILE) !== '1' } }
      : { status: 'signedOut' };
  }
  if (!authConfig.session) return { status: 'signedOut' };
  try {
    const res = await fetch(authConfig.session, { credentials: 'include', headers: { Accept: 'application/json' } });
    return await interpretMe(res);
  } catch {
    return { status: 'error' };
  }
}

/** Remember that this device had a session, so a later 401 reads as "expired" rather than "new visitor". */
export const markHadSession = (): void => store.set(HAD_SESSION, '1');
export const consumeHadSession = (): boolean => {
  const had = store.get(HAD_SESSION) === '1';
  store.del(HAD_SESSION);
  return had;
};

/**
 * Start Google sign-in with a full-page navigation (no popup, no Google SDK,
 * no client ID or secret in the frontend). Demo mode only sets a local flag.
 */
export function startGoogleSignIn(): 'demo' | 'redirect' | 'unconfigured' {
  if (isDemo) {
    store.set(DEMO_KEY, '1');
    return 'demo';
  }
  if (!authConfig.login) return 'unconfigured';
  const returnTo = returnToFor(window.location.hash);
  const sep = authConfig.login.includes('?') ? '&' : '?';
  window.location.assign(`${authConfig.login}${sep}returnTo=${encodeURIComponent(returnTo)}`);
  return 'redirect';
}

/** First-login profile. Demo: local only. Api: POST to VITE_AUTH_PROFILE_URL (contract unconfirmed). */
export async function saveFirstProfile(draft: ProfileDraft): Promise<void> {
  if (isDemo) {
    store.set(DEMO_PROFILE, '1');
    return;
  }
  if (!authConfig.profile) throw new Error('profile endpoint not configured');
  const res = await fetch(authConfig.profile, {
    method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...CSRF_HEADER },
    body: JSON.stringify({ ...draft, primary: draft.primary || null, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }),
  });
  if (!res.ok) throw new Error(`profile ${res.status}`);
}

/**
 * End the session. Api mode: POST to VITE_AUTH_LOGOUT_URL with the CSRF header
 * (the server revokes the session row and clears the HttpOnly cookie; 204).
 * A failed call still signs out locally; the server session then ends at its expiry.
 */
export async function signOut(): Promise<void> {
  store.del(HAD_SESSION);
  if (isDemo) {
    store.del(DEMO_KEY);
    return;
  }
  if (!authConfig.logout) return;
  try {
    await fetch(authConfig.logout, { method: 'POST', credentials: 'include', headers: { ...CSRF_HEADER } });
  } catch {
    /* network failure: local sign-out still happens */
  }
}

/** Fired by the data API on 401 so the gate can return to sign-in ("session ended"). */
export const UNAUTHENTICATED_EVENT = 'ugain:unauthenticated';

export const legalLinks = {
  privacy: env.VITE_PRIVACY_URL || null,
  terms: env.VITE_TERMS_URL || null,
};
